<script>
  import { getApiUrl } from '../api';
  import { getCurrencySymbol, formatDate } from '../utils';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';
  import { navigate } from 'svelte-routing';

  export let showModal = false;
  export let userId = null;
  export let userName = 'User';
  export let onClose;

  let transactions = [];
  let isLoading = true;
  let currency = '$';

  let page = 1;
  const pageSize = 10;
  let totalPages = 1;

  $: currency = getCurrencySymbol($selectedCountry);

  $: if (showModal && userId) {
    page = 1; 
    fetchTransactions();
  }

  async function fetchTransactions() {
    isLoading = true;
    try {
      const res = await fetch(getApiUrl(`/transactions/public/${userId}?page=${page}&pageSize=${pageSize}`));
      const result = await res.json();
      
      if (result && result.data) {
          transactions = result.data;
          totalPages = result.totalPages || 1;
      } else {
          transactions = Array.isArray(result) ? result : [];
          totalPages = 1;
      }
    } catch(e) {
      console.error(e);
    } finally {
      isLoading = false;
    }
  }

  function nextPage() {
      if (page < totalPages) {
          page++;
          fetchTransactions();
      }
  }

  function prevPage() {
      if (page > 1) {
          page--;
          fetchTransactions();
      }
  }

  function closeAndNavigate(promoId) {
      if (onClose) onClose();
      showModal = false;
      navigate(`/promotions/detail/${promoId}`);
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={onClose}>
    <div class="modal-content" on:click|stopPropagation>
      
      <div class="modal-header">
        <h2>{userName}'s {$t('transactions_modal.title')}</h2>
        <button class="close-btn" on:click={onClose}>&times;</button>
      </div>

      <div class="modal-body">
        {#if isLoading}
          <p class="loading">{$t('common.loading')}</p>
        {:else if transactions.length === 0}
          <p class="no-data">{$t('transactions_modal.no_data')}</p>
        {:else}
          <div class="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>{$t('transactions_modal.date')}</th>
                  <th>{$t('transactions_modal.promotion')}</th>
                  <th>{$t('transactions_modal.amount')}</th>
                  <th>{$t('transactions_modal.reward')}</th>
                  <th class="center-col">{$t('transactions_modal.receipt')}</th>
                </tr>
              </thead>
              <tbody>
                {#each transactions as tx}
                  <tr>
                    <td>{formatDate(tx.transactionDateTime)}</td>
                    <td>
                      {#if tx.promotionId}
                         <!-- svelte-ignore a11y-invalid-attribute -->
                         <a href="javascript:void(0)" class="promo-link" on:click={() => closeAndNavigate(tx.promotionId)}>
                             {tx.promotionName || 'Unknown Promotion'}
                         </a>
                      {:else}
                         {tx.promotionName || 'Unknown Promotion'}
                      {/if}
                    </td>
                    <td class="amount-col">{currency}{tx.amount.toFixed(2)}</td>
                    <td class="reward-col">{currency}{(tx.rewardAmount || 0).toFixed(2)}</td>
                    <td class="center-col">
                      <!-- SECURITY: NO IMAGE URL RENDERED. ONLY STATUS ICONS -->
                      {#if tx.verificationStatus === 'Verified'}
                        <span title={$t('transactions_modal.status_verified')} class="status-icon">✅</span>
                      {:else if tx.verificationStatus === 'Rejected'}
                        <span title={$t('transactions_modal.status_rejected')} class="status-icon">❌</span>
                      {:else if tx.verificationStatus === 'N/A'}
                        <span title={$t('transactions_modal.status_na')} class="status-icon">➖</span>
                      {:else if tx.hasReceipt}
                        <span title={$t('transactions_modal.status_pending')} class="status-icon">📄</span>
                      {:else}
                        <span class="no-receipt">-</span>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>

          {#if totalPages > 1}
            <div class="pagination">
                <button disabled={page <= 1} on:click={prevPage}>{$t('common.prev')}</button>
                <span>{$t('common.page')} {page} {$t('common.of')} {totalPages}</span>
                <button disabled={page >= totalPages} on:click={nextPage}>{$t('common.next')}</button>
            </div>
          {/if}
        {/if}
      </div>
      
      <div class="modal-footer">
          <button class="done-btn" on:click={onClose}>{$t('transactions_modal.close')}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.6); z-index: 2000;
    display: flex; justify-content: center; align-items: center;
    padding: 1rem; box-sizing: border-box;
  }
  .modal-content {
    background: #fff; width: 100%; max-width: 700px;
    border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    display: flex; flex-direction: column; max-height: 90vh;
  }
  .modal-header {
    display: flex; justify-content: space-between; align-items: center;
    background: #f8f9fa; padding: 1rem 1.5rem; border-bottom: 1px solid #eee;
  }
  .modal-header h2 { margin: 0; font-size: 1.25rem; color: #333; }
  .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #666; }
  
  .modal-body { 
      padding: 1.5rem; 
      overflow-y: auto; 
  }
  
  .table-responsive {
      border: 1px solid #eee;
      border-radius: 6px;
      overflow: auto;
  }
  table { width: 100%; border-collapse: collapse; text-align: left; font-size: 0.95rem; white-space: nowrap; }
  th, td { padding: 0.8rem; border-bottom: 1px solid #eee; }
  th { background-color: #f8f9fa; font-weight: 600; color: #555; }
  tr:last-child td { border-bottom: none; }
  .center-col { text-align: center; }
  
  .promo-link {
      color: #007bff;
      text-decoration: none;
      font-weight: 500;
  }
  .promo-link:hover {
      text-decoration: underline;
      color: #0056b3;
  }
  .status-icon { font-size: 1.2rem; cursor: help; }
  .no-receipt { color: #aaa; }

  .amount-col { font-weight: bold; color: #333; }
  .reward-col { font-weight: bold; color: #28a745; }

  .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-top: 1.5rem;
      font-size: 0.9rem;
      color: #555;
  }
  .pagination button {
      background: #e9ecef; border: none; padding: 0.4rem 1rem;
      border-radius: 4px; cursor: pointer; color: #333;
  }
  .pagination button:hover:not(:disabled) { background: #dee2e6; }
  .pagination button:disabled { opacity: 0.5; cursor: not-allowed; }

  .modal-footer {
      padding: 1rem 1.5rem; border-top: 1px solid #eee;
      text-align: right; background: #fafafa;
  }
  .done-btn {
      background: #6c757d; color: white; border: none; padding: 0.6rem 1.5rem;
      border-radius: 4px; cursor: pointer; font-weight: 500; font-size: 1rem;
  }
  .done-btn:hover { background: #5a6268; }

  .loading, .no-data { text-align: center; color: #666; font-style: italic; padding: 2rem 0; }

  @media (max-width: 600px) {
      .modal-content { border-radius: 0; max-height: 100vh; height: 100%; }
      .modal-footer { margin-top: auto; padding-bottom: 2rem; }
  }
</style>